import { getLocale } from '@/utils/i18n';
import { partnerSupplier } from '@/utils/environment';
import { Platform } from 'react-native';
import { SessionService } from '@domain/auth/blocs/services/session-service';
import {
  USER_SETTINGS_KEY,
  UserSettingsAdapter,
} from '@/adapters/user-settings';

/**
 * Static headers that are computed once at module initialization.
 * These values don't change during the app lifecycle.
 */
const STATIC_HEADERS: Record<string, string> = {
  'Accept-Language': getLocale(),
  'X-Pepper-Accept-Language': getLocale(),
  'X-Pepper-App-Platform': Platform.OS,
  'X-Pepper-Business-Organization-ID': partnerSupplier.businessOrganizationUUID,
  'X-Pepper-Container-App': 'JALAPENO',
};

interface HeaderBuilderConfig {
  authenticated: boolean;
  contentType: boolean;
  customHeaders: Record<string, string>;
  tokenOverride?: string;
}

/**
 * Fluent builder for constructing HTTP headers.
 *
 * @example
 * // Anonymous request
 * const headers = await HeaderBuilder.create().build();
 *
 * // Authenticated request with JSON body
 * const headers = await HeaderBuilder.create()
 *   .withAuth()
 *   .withContentType()
 *   .build();
 *
 * // Authenticated request with custom header
 * const headers = await HeaderBuilder.create()
 *   .withAuth()
 *   .withContentType()
 *   .withHeader('X-Custom-Header', 'value')
 *   .build();
 */
class HeaderBuilderImpl {
  private config: HeaderBuilderConfig = {
    authenticated: false,
    contentType: false,
    customHeaders: {},
  };

  /**
   * Include authentication headers (Authorization + business context).
   */
  withAuth(tokenOverride?: string): HeaderBuilderImpl {
    this.config.authenticated = true;
    this.config.tokenOverride = tokenOverride;
    return this;
  }

  /**
   * Include Content-Type: application/json header.
   */
  withContentType(): HeaderBuilderImpl {
    this.config.contentType = true;
    return this;
  }

  /**
   * Add a custom header.
   */
  withHeader(key: string, value: string): HeaderBuilderImpl {
    this.config.customHeaders[key] = value;
    return this;
  }

  /**
   * Build the final headers object.
   */
  async build(): Promise<Record<string, string>> {
    const headers: Record<string, string> = { ...STATIC_HEADERS };

    if (this.config.authenticated) {
      const token =
        this.config.tokenOverride || (await SessionService.getIdToken());
      headers['Authorization'] = token ? `Bearer ${token}` : '';

      try {
        const adapter = new UserSettingsAdapter<{
          selectedSupplierUUID?: string;
        }>(USER_SETTINGS_KEY);
        const settings = await adapter.fetch();
        if (settings?.selectedSupplierUUID) {
          headers['X-Pepper-Business-ID'] = settings.selectedSupplierUUID;
        }
      } catch {
        // User settings are optional
      }
    }

    if (this.config.contentType) {
      headers['Content-Type'] = 'application/json';
    }

    return {
      ...headers,
      ...this.config.customHeaders,
    };
  }
}

/**
 * Factory for creating header builders.
 */
export const HeaderBuilder = {
  create: () => new HeaderBuilderImpl(),
};
